/**
 * Geometria do símbolo do Arcus.
 *
 * Os números aqui não foram desenhados: foram **medidos** a partir de
 * `public/arcus-simbolo.svg`, amostrando a arte renderizada a 990px e lendo,
 * para cada anel, o ângulo de início/fim de cada arco e o alfa da cor sobre o
 * navy de fundo. Por isso são valores quebrados — são a marca, não uma
 * aproximação dela.
 *
 * O que a medição revelou, e que o arquivo original escondia atrás de máscaras
 * e PNGs embutidos: o símbolo é **um só desenho paramétrico**. Cinco anéis
 * concêntricos de mesma espessura (~34.7), espaçados de ~75.3, e cada anel é um
 * arco que nasce transparente e ganha opacidade linearmente até virar cor
 * chapada. É uma cauda de cometa — ou, na leitura do produto, um facho de farol.
 *
 * É essa regularidade que torna a marca animável: girar cada anel em torno do
 * centro reproduz exatamente o gesto que o desenho já congela.
 *
 * Convenção de ângulo: **graus no sentido horário a partir das 12h**, com o
 * centro do símbolo na origem `(0, 0)`. Escolhido para casar com a leitura
 * natural de um facho girando, e para que a origem de rotação seja `(0,0)`.
 */

/** Papel de cor de um anel. O tom concreto é resolvido pelo componente. */
export type ArcusRingTone = 'lamp' | 'brand' | 'accent';

export interface ArcusRingArc {
  /** Onde a cauda transparente começa. */
  from: number;
  /** Onde a cauda termina e a cor chapada começa. Igual a `to` se não há cabeça. */
  solidFrom: number;
  /** Fim do arco. */
  to: number;
}

export interface ArcusRing {
  radius: number;
  tone: ArcusRingTone;
  /**
   * Se o anel participa da varredura.
   *
   * A lâmpada fica de fora: girar um círculo completo não produz nenhum efeito
   * visível e só gastaria composição. A velocidade de cada anel que gira não
   * mora aqui — sai de `describeSweepSpeeds`, em função da posição.
   */
  spins: boolean;
  /** Arcos do anel. Vazio significa anel completo (360°). */
  arcs: ArcusRingArc[];
}

/**
 * Espessura única para todos os anéis.
 *
 * As medições deram 34.83 / 34.70 / 34.69 / 34.57 / 34.45 — variação de 0.38 em
 * 990, ou seja, sub-pixel em qualquer tamanho de render real. Unificar em um
 * valor só é o que o desenho sempre quis dizer.
 */
export const ARCUS_RING_WIDTH = 34.7;

/**
 * Os cinco anéis, do centro para fora.
 *
 * Os vãos entre arcos (ex.: 352°→122° no anel 1) são parte do desenho: é a
 * abertura que faz o arco ser um facho e não um círculo.
 */
export const ARCUS_RINGS: readonly ArcusRing[] = [
  // Lâmpada: o único anel fechado. É o ponto central da marca.
  { radius: 58.02, tone: 'lamp', spins: false, arcs: [] },
  {
    radius: 132.06,
    tone: 'brand',
    spins: true,
    // Cauda de 155° seguida de 75° de azul chapado.
    arcs: [{ from: 122, solidFrom: 277, to: 352 }],
  },
  {
    radius: 207.24,
    tone: 'accent',
    spins: true,
    // Único anel com dois arcos, e nenhum deles chega a fechar em cor sólida.
    arcs: [
      { from: 224, solidFrom: 398, to: 398 },
      { from: 108, solidFrom: 174, to: 174 },
    ],
  },
  {
    radius: 283.38,
    tone: 'lamp',
    spins: true,
    arcs: [{ from: 25, solidFrom: 176.5, to: 176.5 }],
  },
  {
    radius: 359.19,
    tone: 'accent',
    spins: true,
    // Cauda de 137° e a cabeça âmbar mais longa da marca, 87°.
    arcs: [{ from: 268, solidFrom: 405, to: 492 }],
  },
];

/** Raio externo da arte, usado para dimensionar a viewBox sem cortar traço. */
export const ARCUS_MARK_RADIUS = 359.19 + ARCUS_RING_WIDTH / 2;

/**
 * Sobreposição, em graus, entre a cauda e a cabeça de um mesmo arco.
 *
 * Sem ela os dois traços se encostam por pontas retas, e cada ponta é
 * antialiasada contra o fundo por conta própria: o resultado é uma costura de
 * 1px visível na emenda, justamente onde a cor deveria ser contínua. Como a
 * cauda chega na emenda já com opacidade 1, sobrepor é invisível.
 */
export const ARCUS_ARC_JOIN_OVERLAP = 1.5;

/**
 * Velocidade do anel mais externo, relativa à do mais interno.
 *
 * `1` faria os anéis girarem travados entre si, e a marca leria como um logo
 * girando inteiro — o gesto errado. Abaixo de `1` os externos ficam para trás e
 * a abertura entre os arcos muda ao longo da volta, que é o que produz a leitura
 * de luz se propagando para fora.
 */
export const ARCUS_OUTER_SPEED = 0.64;

/**
 * Velocidade de cada anel que gira, do interno para o externo.
 *
 * Progressão **geométrica** entre `1` e `outerSpeed`, e não linear: o que o olho
 * compara é a razão entre velocidades vizinhas, não a diferença. Numa
 * progressão linear os últimos anéis ficariam proporcionalmente muito mais
 * separados que os primeiros, e a onda perderia a cadência.
 *
 * Aceita `outerSpeed` acima de `1`, que inverte a leitura: os externos passam na
 * frente e a luz parece convergir para o centro.
 */
export function describeSweepSpeeds(count: number, outerSpeed = ARCUS_OUTER_SPEED): number[] {
  if (count <= 0) return [];
  // Com um anel só não há razão a distribuir — ele gira na velocidade base.
  if (count === 1) return [1];

  return Array.from({ length: count }, (_, index) => outerSpeed ** (index / (count - 1)));
}

/**
 * Quanto uma ponta arredondada avança além do fim do arco, **em graus**.
 *
 * `stroke-linecap: round` fecha a ponta com um semicírculo de raio igual a meia
 * espessura do traço. Esse semicírculo não cabe dentro do arco: ele se soma nas
 * duas extremidades. Como o avanço é uma distância fixa (17.35) sobre uma
 * circunferência, o efeito **em ângulo** depende do raio — no anel interno vale
 * 7.5°, no externo 2.8°.
 *
 * É por isso que arredondar não é só trocar uma propriedade de CSS: sem descontar
 * este ângulo das duas pontas, todo arco fica mais comprido e as aberturas do
 * desenho — que são o que faz a marca ser um facho e não um círculo — encolhem
 * de forma desigual entre os anéis.
 */
export function describeCapAngle(radius: number, strokeWidth = ARCUS_RING_WIDTH): number {
  if (radius <= 0) return 0;

  return ((strokeWidth / 2 / radius) * 180) / Math.PI;
}

export interface ArcusSweep {
  /** Volta do anel de velocidade `1`, em segundos. */
  seconds: number;
  /** Multiplicador de cada anel que gira, do interno para o externo. */
  speeds: readonly number[];
}

/** Quantos anéis participam da varredura. */
const SPINNING_RING_COUNT = ARCUS_RINGS.filter((ring) => ring.spins).length;

/**
 * Ritmos nomeados da varredura.
 *
 * São dois gestos diferentes, não duas velocidades do mesmo gesto:
 *
 * - **`wave`** — progressão geométrica, cada anel mais lento que o de dentro.
 *   Os arcos se abrem em ordem e a luz parece se propagar para fora. Lento o
 *   bastante para não pedir atenção: é o ritmo de tela de abertura.
 *
 * - **`restless`** — quatro períodos próximos (2,5s a 3,3s) e **deliberadamente
 *   desiguais**, calibrados no playground. Não é monotônico: o anel branco é o
 *   mais rápido e o âmbar interno o mais lento. Sem ordem entre eles a figura
 *   bate contra si mesma e nunca se repete igual, o que lê como atividade em vez
 *   de propagação — é o ritmo de espera.
 *
 * Os números de `restless` **não saem de fórmula nenhuma**. Foram escolhidos no
 * olho e medidos; tentar ajustá-los a uma progressão destruiria exatamente a
 * irregularidade que produz o efeito.
 */
export const ARCUS_SWEEPS = {
  wave: { seconds: 14, speeds: describeSweepSpeeds(SPINNING_RING_COUNT) },
  restless: { seconds: 5, speeds: [1.86, 1.53, 2, 1.73] },
} as const satisfies Record<string, ArcusSweep>;

export type ArcusSweepName = keyof typeof ARCUS_SWEEPS;

/** Resolve um preset nomeado ou devolve o ritmo já pronto. */
export function resolveSweep(sweep: ArcusSweepName | ArcusSweep): ArcusSweep {
  return typeof sweep === 'string' ? ARCUS_SWEEPS[sweep] : sweep;
}

export interface ArcusPoint {
  x: number;
  y: number;
}

/**
 * Centro do desenho quando nenhum é informado.
 *
 * Existe para os testes de geometria, que raciocinam melhor sobre a origem. O
 * componente **sempre** passa o centro do canvas — ver `ArcusMark`, onde a
 * explicação de por que o desenho não vive centrado em `(0,0)` está inteira.
 */
const ORIGIN: ArcusPoint = { x: 0, y: 0 };

function pointOnRing(radius: number, degrees: number, center: ArcusPoint): ArcusPoint {
  const radians = (degrees * Math.PI) / 180;
  // Horário a partir das 12h: 0° = topo, 90° = direita. O `-cos` vem do eixo Y
  // do SVG apontar para baixo.
  return {
    x: center.x + radius * Math.sin(radians),
    y: center.y - radius * Math.cos(radians),
  };
}

const round = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * Monta o `d` de um arco de anel, no sentido horário, com centro na origem.
 *
 * Recebe ângulos absolutos (podem passar de 360° — ver `ARCUS_RINGS`, onde
 * arcos que cruzam as 12h são escritos como `268 → 405` em vez de `268 → 45`,
 * justamente para que a diferença continue sendo a varredura real).
 */
export function describeRingArc(
  radius: number,
  fromDegrees: number,
  toDegrees: number,
  center: ArcusPoint = ORIGIN,
): string {
  const sweep = toDegrees - fromDegrees;

  if (sweep <= 0 || sweep >= 360) {
    throw new Error(
      `Arco de ${sweep}° não é representável com um comando A. ` +
        'Use um <circle> para anel completo e ângulos crescentes para o resto.',
    );
  }

  const from = pointOnRing(radius, fromDegrees, center);
  const to = pointOnRing(radius, toDegrees, center);
  const largeArc = sweep > 180 ? 1 : 0;

  return `M ${round(from.x)} ${round(from.y)} A ${radius} ${radius} 0 ${largeArc} 1 ${round(to.x)} ${round(to.y)}`;
}

/**
 * Endpoints do gradiente da cauda, em `userSpaceOnUse`.
 *
 * O gradiente é **linear ao longo da corda** que liga o início da cauda ao
 * início da cabeça — não é um gradiente cônico. Isso não é uma simplificação:
 * é o que o arquivo original faz, e a medição do alfa confirmou que o resultado
 * é praticamente linear no ângulo (desvio máximo de 0.05 em 1.0).
 *
 * Funciona porque toda cauda da marca tem 180° ou menos: até 180°, a projeção
 * dos pontos do arco sobre a corda é monótona, então o alfa nunca volta atrás.
 */
export function describeTailGradient(
  radius: number,
  fromDegrees: number,
  solidFromDegrees: number,
  center: ArcusPoint = ORIGIN,
): { x1: number; y1: number; x2: number; y2: number } {
  const start = pointOnRing(radius, fromDegrees, center);
  const end = pointOnRing(radius, solidFromDegrees, center);

  return { x1: round(start.x), y1: round(start.y), x2: round(end.x), y2: round(end.y) };
}

export type ArcusCaps = 'butt' | 'round';

export interface ArcusArcShape {
  /** Trecho que nasce transparente e ganha opacidade. */
  tail: { d: string; gradient: ReturnType<typeof describeTailGradient> };
  /** Trecho de cor chapada. `null` nos arcos que não fecham em cor sólida. */
  head: { d: string } | null;
}

/**
 * Traduz um arco da marca nos traços que o desenham.
 *
 * Mora aqui, e não no componente, porque **dois consumidores precisam do mesmo
 * resultado**: o `ArcusMark`, que vira JSX, e `scripts/generate-brand-assets.ts`,
 * que escreve os SVGs de `public/`. Reimplementar de um lado é o caminho curto
 * para o arquivo estático e o componente desenharem marcas ligeiramente
 * diferentes — e ninguém percebe até alguém sobrepor os dois.
 *
 * Duas correções ficam encapsuladas aqui:
 *
 * 1. **Desconto da ponta arredondada**, e só nas extremidades **externas**. A
 *    emenda entre cauda e cabeça é interior: recuá-la abriria um vão no meio da
 *    cor.
 * 2. **Sobreposição na emenda**, porque duas pontas encostadas são antialiasadas
 *    contra o fundo cada uma por sua conta e deixam uma costura de 1px visível.
 */
export function describeArcShape(
  radius: number,
  arc: ArcusRingArc,
  center: ArcusPoint = ORIGIN,
  caps: ArcusCaps = 'round',
): ArcusArcShape {
  const cap = caps === 'round' ? describeCapAngle(radius) : 0;
  const hasHead = arc.to > arc.solidFrom;
  const tailFrom = arc.from + cap;
  const tailTo = hasHead ? arc.solidFrom : arc.solidFrom - cap;

  return {
    tail: {
      d: describeRingArc(radius, tailFrom, tailTo, center),
      gradient: describeTailGradient(radius, tailFrom, tailTo, center),
    },
    head: hasHead
      ? { d: describeRingArc(radius, arc.solidFrom - ARCUS_ARC_JOIN_OVERLAP, arc.to - cap, center) }
      : null,
  };
}
